import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {
  getDateFromInterval,
  getGuestContactInfo,
  selectDefaultChannel,
} from "utilities/helperFunctions";
import useScheduledMessagePanelStyles from "styles/useScheduledMessagePanelStyles";
import clsx from "clsx";
import {format} from "date-fns";
import {createAction} from "redux/actions/experiencesActions";
import {addNotifications} from "redux/actions/notificationsActions";
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Typography,
} from "@material-ui/core";
import CloseIconButton from "core/buttons/CloseIconButton";
import MessageSelector from "components/MultiOption/MessageSelector";
import FlexContainer from "connectui/component/layout/FlexContainer";
import SendInput from "components/TextFields/SendInput";
import FilledTextField from "core/inputs/FilledTextField";
import FilledSelect from "core/selects/FilledSelect";

const channels = {
  0: "SMS",
  1: "airbnb",
  2: "email",
  3: "whatsapp",
  4: "vrbo",
  5: "homeaway",
  6: "booking_com",
};

const CreateAutoMessageMenu = ({
  guestId,
  isMobile,
  availableChannels,
  whatsappData = {},
  closeMenu,
  subMenuOpen,
  setSubMenuOpen = () => {},
}) => {
  const classes = useScheduledMessagePanelStyles();
  const dispatch = useDispatch();

  const guests = useSelector((state) => state.defaultReducer.guests_dict);
  const user_profile = useSelector(
    (state) => state.defaultReducer.user_profile,
  );

  const selectedGuest = useMemo(() => guests[guestId] ?? {}, [guests, guestId]);
  const is_inquiry = selectedGuest.stage === "lead";
  const visibleChannels = useMemo(() => {
    const channelsData = {};
    availableChannels.forEach((c) => {
      channelsData[c.id] = c.show;
    });
    return channelsData;
  }, [availableChannels]);
  const airbnbHidden = !availableChannels.find((c) => c.id === "airbnb")?.show;

  const [channel, setChannel] = useState(-1);
  const [delay, setDelay] = useState(3);
  const [unit, setUnit] = useState("hours");
  const [cancelActive, setCancelActive] = useState(is_inquiry);
  const [whatsappTemplateID, setWhatsappTemplateID] = useState(null);

  const {guest_email, guest_phone} = useMemo(
    () => getGuestContactInfo(selectedGuest),
    [selectedGuest],
  );
  const cancelDate = useMemo(
    () => getDateFromInterval(delay, unit),
    [delay, unit],
  );

  useEffect(() => {
    setChannel((prev) => getInitialChannel());
  }, [guestId]);

  const getInitialChannel = () => {
    return selectDefaultChannel({
      guest: selectedGuest,
      userProfile: user_profile,
      guestPhone: guest_phone,
      guestEmail: guest_email,
      airbnbHidden,
    });
  };
  const resetChannel = () => setChannel((prev) => getInitialChannel());

  const handleSchedule = (message) => {
    let trigger = {};
    let action = null;
    const timestamp = getDateFromInterval(delay, unit)?.getTime();

    if (!!Number(delay)) {
      if (!!timestamp) {
        trigger.trigger = "specific_time";
        trigger.trigger_value = timestamp;
      }
      if (cancelActive) {
        trigger.conditions = [
          {
            c_type: "bool",
            field: "guest_updated_at",
            operator: "<",
            val: new Date().getTime(),
          },
          {
            c_type: "bool",
            field: "guest_stage",
            val: "lead",
          },
        ];
      }
    } else {
      trigger.trigger = "now";
    }

    if (channel === 2) {
      // if channel === 'email'
      action = {
        action_id: "email",
        auto_translate: true,
        subject: [{language: "en", value: message.subject}],
        body: [{language: "en", value: message.html}],
        header: [],
        template: null,
      };
    } else if (channel == 3) {
      action = {
        action_id: "whatsapp",
        integration_template_id: whatsappTemplateID,
        service_id: whatsappData.accountId,
      };
    } else if (channel > 3 && channel < 7) {
      action = {
        action_id: "ota_email",
        auto_translate: true,
        body: [{language: "en", value: message}],
        template: null,
        channel: channels[channel],
      };
    } else {
      action = {
        action_id: channels[channel],
        auto_translate: true,
        body: [{language: "en", value: message}],
      };
    }

    dispatch(
      createAction({
        trigger: !!Object.keys(trigger).length ? trigger : null,
        guestId,
        action,
        disableAlerts: true,
        customErrorMessage: "Failed to schedule message",
        customErrorDescription: "Please try again later",
        onSuccess: (response) => {
          dispatch(addNotifications(response.action));
        },
      }),
    );
    closeMenu();
  };

  return (
    <FlexContainer
      flexDirection="column"
      sx={{
        maxHeight: "350px",
        overflow: "hidden auto",
        width: isMobile ? "100%" : "500px",
      }}
    >
      <div className={classes.titleRow} style={{width: "100%"}}>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          flexWrap="wrap"
          style={{gap: "8px"}}
        >
          {!isMobile && (
            <Typography variant="h1" className={classes.header}>
              {"Schedule Message"}
            </Typography>
          )}
          <MessageSelector
            variant="secondary"
            slc_channel={channel}
            selectChannel={setChannel}
            airbnb={visibleChannels.airbnb}
            SMS={visibleChannels.SMS}
            email={visibleChannels.email}
            whatsapp={visibleChannels.whatsapp}
            vrbo={visibleChannels.vrbo}
            bookingcom={visibleChannels.booking_com}
            homeaway={visibleChannels.homeaway}
            expedia={visibleChannels.expedia}
          />
        </Box>
        {!isMobile && <CloseIconButton sm onClick={() => closeMenu()} />}
      </div>
      <div style={{width: "100%"}}>
        <SendInput
          schedule
          hideChatbot
          disableGutters
          defaultBody={
            is_inquiry
              ? `Hi ${selectedGuest.name}, \nAre you still interested in booking? Please let us know if you have any questions about the listing or your stay.`
              : ""
          }
          disableAttachments={true}
          guest={selectedGuest}
          guest_email={guest_email}
          current_user={user_profile.enso_key}
          user_profile={user_profile}
          slc_channel={channel}
          resetSelectedChannel={resetChannel}
          handleSaveScheduled={handleSchedule}
          templates={whatsappData.templates}
          whatsappAccountId={whatsappData.accountId}
          selectedTemplateId={whatsappTemplateID}
          setSubMenuOpen={setSubMenuOpen}
          setSelectedTemplateId={(templateId) =>
            setWhatsappTemplateID(templateId)
          }
        />
        <div className={classes.mt}>
          <FilledTextField
            placeholder="0"
            value={delay}
            type="number"
            disableRadius
            inputProps={{min: 0, max: 99999}}
            className={classes.leftInput}
            onChange={(e) => setDelay(e.target.value)}
          />
          <FilledSelect
            value={unit}
            disabled={subMenuOpen}
            className={classes.rightInput}
            onOpen={() => setSubMenuOpen(true)}
            onClose={() => setSubMenuOpen(false)}
            onChange={(e) => setUnit(e.target.value)}
          >
            <MenuItem selected={unit === "seconds"} value={"seconds"}>
              {"seconds"}
            </MenuItem>
            <MenuItem selected={unit === "minutes"} value={"minutes"}>
              {"minutes"}
            </MenuItem>
            <MenuItem selected={unit === "hours"} value={"hours"}>
              {"hours"}
            </MenuItem>
            <MenuItem selected={unit === "days"} value={"days"}>
              {"days"}
            </MenuItem>
          </FilledSelect>
        </div>
        {!!Number(delay) && (
          <div className={clsx(classes.cancelOption, classes.mt)}>
            <FormControlLabel
              style={{marginLeft: 0}}
              className={classes.formControlLabel}
              control={
                <Checkbox
                  size="small"
                  checked={cancelActive}
                  className={classes.checkbox}
                  onChange={(e) => setCancelActive(e.target.checked)}
                />
              }
              label={
                <Typography variant="button" className={classes.text}>
                  {`Cancel this message if guest books or replies before ${format(cancelDate, "MMMM d")} at ${format(cancelDate, "h:mmaaa")}`}
                </Typography>
              }
            />
          </div>
        )}
      </div>
    </FlexContainer>
  );
};

export default CreateAutoMessageMenu;
